import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  PATIENT_SEXES,
  PATIENT_TYPES,
  type PatchPatientInput,
  type Patient,
  type PatientSex,
} from '@/api/patients';
import type { ClinicResponse } from '@/api/hospital';
import type { PatientFlagsState } from '@/lib/patientFlags';
import { clinicCustomFieldInputs } from '@/lib/hospitalCustomFields';
import { customFieldsForClinic, hospitalAttributesPatch } from '@/lib/hospitalAttributes';
import { PatientFlagsSection } from './PatientFlagsSection';

type Props = {
  open: boolean;
  initial: Patient | null;
  /** Patient's primary clinic — drives the flag-row visibility gates
   *  + the "clinic default" displayed alongside each row. When omitted
   *  the flags section is hidden (rail can pass it once it's loaded). */
  clinic?: ClinicResponse;
  /** Logged-in clinician's flags — drives which override rows the
   *  clinician is allowed to see at all (per the
   *  `clinician_patient_allow_*_toggle` gates). Hides the flags
   *  section when omitted. */
  clinicianFlags?: Record<string, unknown>;
  onCancel: () => void;
  onSubmit: (body: PatchPatientInput) => Promise<void>;
  pending?: boolean;
};

const schema = z.object({
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().optional(),
  sex: z.enum([...PATIENT_SEXES, ''] as unknown as ['', PatientSex, ...PatientSex[]]).optional(),
  birthday: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{4}-\d{2}-\d{2}$/.test(v), 'Use YYYY-MM-DD'),
  patient_type: z.string().optional(),
  meal_time_breakfast: z.string().optional(),
  meal_time_lunch: z.string().optional(),
  meal_time_dinner: z.string().optional(),
});

const padTime = (t: string | undefined): string | undefined => {
  if (!t) return undefined;
  return t.length === 5 ? `${t}:00` : t;
};
const trimTime = (t: string | undefined | null): string => (t ? t.slice(0, 5) : '');

/** Strip falsy strings so we send `undefined` (= keep existing) vs empty (= unset). */
const cleanEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === undefined) continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
};

export const EditPatientDialog = ({
  open,
  initial,
  clinic,
  clinicianFlags,
  onCancel,
  onSubmit,
  pending,
}: Props) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [sex, setSex] = useState<string>('');
  const [birthday, setBirthday] = useState('');
  const [patientType, setPatientType] = useState<string>('');
  const [breakfast, setBreakfast] = useState('');
  const [lunch, setLunch] = useState('');
  const [dinner, setDinner] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Flags state — seeded from the patient's existing `flags_raw`
  // overrides. Snapshot the initial value too so we can submit just the
  // diff (PATCH semantics — only send keys the clinician actually
  // changed).
  const [flags, setFlags] = useState<PatientFlagsState>({});
  const [initialFlags, setInitialFlags] = useState<PatientFlagsState>({});
  // Clinic-defined external id + custom fields. Gated on the PATIENT'S
  // clinic flags, not the clinician's, matching the Add dialog — a
  // clinician at clinics under different hospitals gets the right form
  // for the clinic this patient is actually in.
  const [externalId, setExternalId] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, string>>({});

  const clinicFlags = (clinic?.flags ?? {}) as Record<string, unknown>;
  const hasExternalId = clinicFlags.hospital_patient_has_external_id === true;
  const customFieldDefs = useMemo(() => clinicCustomFieldInputs(clinic), [clinic]);
  const showHospitalFields = hasExternalId || customFieldDefs.length > 0;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setFirstName(initial?.first_name ?? '');
    setLastName(initial?.last_name ?? '');
    setEmail(initial?.email ?? '');
    setPhone(initial?.phone_number ?? '');
    setSex(((initial as Record<string, unknown>)?.sex as string) ?? '');
    setBirthday(((initial as Record<string, unknown>)?.birthday as string) ?? '');
    setPatientType(((initial as Record<string, unknown>)?.patient_type as string) ?? '');
    setBreakfast(trimTime((initial as Record<string, unknown>)?.meal_time_breakfast as string));
    setLunch(trimTime((initial as Record<string, unknown>)?.meal_time_lunch as string));
    setDinner(trimTime((initial as Record<string, unknown>)?.meal_time_dinner as string));
    // Server's `flags_raw` can carry `null` for cleared overrides;
    // the dialog state models them as `undefined` ("inherit clinic
    // default") because React's `<select value={null}>` warns and the
    // section's row logic uses `===` checks against the canonical
    // primitives. Normalize on the way in.
    const rawIn = (initial?.flags_raw ?? {}) as Record<string, unknown>;
    const normalized: PatientFlagsState = {};
    for (const [k, v] of Object.entries(rawIn)) {
      if (v === null) continue;
      (normalized as Record<string, unknown>)[k] = v;
    }
    setFlags(normalized);
    setInitialFlags(normalized);

    setExternalId(initial?.hospital_attributes?.patient_external_id ?? '');
    // Seeded through the clinic filter, never straight from the bag: the
    // server merges attributes across every hospital the caller can see
    // (FOOD-1069), so the bag can hold another hospital's keys and this
    // form must not adopt them.
    setCustomFields(
      customFieldsForClinic(customFieldDefs, initial?.hospital_attributes),
    );
    // customFieldDefs is derived from `clinic`, which is in the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial, clinic]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phone || undefined,
      sex: sex || '',
      birthday: birthday || undefined,
      patient_type: patientType || undefined,
      meal_time_breakfast: padTime(breakfast),
      meal_time_lunch: padTime(lunch),
      meal_time_dinner: padTime(dinner),
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('Please review the form.'));
      return;
    }
    // The server's PATCH semantics: missing field = keep current value.
    // Send only what's actually changed, and never send empty strings.
    const body: PatchPatientInput = cleanEmpty({
      first_name: parsed.data.first_name,
      last_name: parsed.data.last_name,
      email: parsed.data.email,
      phone_number: parsed.data.phone_number,
      sex: parsed.data.sex && parsed.data.sex !== initial?.sex
        ? (parsed.data.sex as PatientSex)
        : undefined,
      birthday: parsed.data.birthday !== initial?.birthday ? parsed.data.birthday : undefined,
      patient_type: parsed.data.patient_type as PatchPatientInput['patient_type'],
      meal_time_breakfast: parsed.data.meal_time_breakfast,
      meal_time_lunch: parsed.data.meal_time_lunch,
      meal_time_dinner: parsed.data.meal_time_dinner,
    });
    // Diff the flags against the snapshot the dialog was opened with.
    // For each key that DIFFERS, send the new value (or null when the
    // clinician cleared an override, so the server reverts to clinic
    // default). Unchanged keys are omitted to keep the PATCH minimal.
    const flagsDiff: Record<string, string | boolean | number | null> = {};
    const allKeys = new Set([...Object.keys(initialFlags), ...Object.keys(flags)]);
    for (const k of allKeys) {
      const prev = initialFlags[k as keyof PatientFlagsState];
      const next = flags[k as keyof PatientFlagsState];
      if (prev === next) continue;
      // `undefined` in our state == "use clinic default" — represent on
      // the wire as `null` so the server clears the override.
      flagsDiff[k] = next === undefined ? null : (next as string | boolean | number);
    }
    if (Object.keys(flagsDiff).length > 0) {
      body.flags_raw = flagsDiff;
    }
    // Omitted entirely when nothing changed. The write replaces the stored
    // values wholesale and is refused with a 403 for a caller without
    // rights to this hospital, so an untouched dialog must not send it.
    const attributes = hospitalAttributesPatch({
      hospitalId: clinic?.hospital_id,
      defs: customFieldDefs,
      hasExternalId,
      externalId,
      customFields,
      initial: initial?.hospital_attributes,
    });
    if (attributes) {
      body.hospital_attributes = attributes;
    }
    try {
      await onSubmit(body);
    } catch {
      setError(t('Could not save changes.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('Edit patient')}</DialogTitle>
          <DialogDescription>
            {t('Empty fields are kept as-is. Only what you change is saved.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ep-first">{t('First name')}</Label>
              <Input id="ep-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ep-last">{t('Last name')}</Label>
              <Input id="ep-last" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ep-email">{t('Email')}</Label>
            <Input id="ep-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ep-phone">{t('Phone')}</Label>
            <Input id="ep-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ep-sex">{t('Sex')}</Label>
              <select
                id="ep-sex"
                value={sex}
                onChange={(e) => setSex(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">—</option>
                {PATIENT_SEXES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ep-bday">{t('Birthday')}</Label>
              <Input id="ep-bday" type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
            </div>
          </div>
          {/*
           * `patient_type` is being phased out — hidden from the form per the
           * team's request. Existing values still round-trip through PATCH
           * (we read `initial.patient_type` into local state but don't show
           * an editor), so editing other fields doesn't accidentally erase
           * the type. See FOLLOW_UPS.md "Deprecated v1 surfaces".
           */}
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && (
          <div className="space-y-1">
            <Label htmlFor="ep-type">{t('Patient type')}</Label>
            <select
              id="ep-type"
              value={patientType}
              onChange={(e) => setPatientType(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">—</option>
              {PATIENT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="ep-breakfast">{t('Breakfast')}</Label>
              <Input id="ep-breakfast" type="time" value={breakfast} onChange={(e) => setBreakfast(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ep-lunch">{t('Lunch')}</Label>
              <Input id="ep-lunch" type="time" value={lunch} onChange={(e) => setLunch(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ep-dinner">{t('Dinner')}</Label>
              <Input id="ep-dinner" type="time" value={dinner} onChange={(e) => setDinner(e.target.value)} />
            </div>
          </div>
          {/* --- External ID + custom fields (per-clinic, conditional) ---
               Mirrors the Add dialog. Both are gated by the PATIENT'S
               clinic flags, not the clinician's, so a clinician at clinics
               under different hospitals edits the right set of fields.
               Until FOOD-1071 these could only be set at Add time. --- */}
          {showHospitalFields && (
            <div className="space-y-3 rounded-md border bg-muted/20 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('Hospital fields')}
              </p>
              {hasExternalId && (
                <div className="space-y-1">
                  <Label htmlFor="ep-external-id">{t('Patient ID')}</Label>
                  <Input
                    id="ep-external-id"
                    value={externalId}
                    onChange={(e) => setExternalId(e.target.value)}
                    placeholder={t('e.g. MRN-12345')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("Hospital's record identifier for this patient. It can be changed but not removed here.")}
                  </p>
                </div>
              )}
              {customFieldDefs.map((def) => (
                <div key={def.field} className="space-y-1">
                  <Label htmlFor={`ep-cf-${def.field}`}>{def.label}</Label>
                  {def.values ? (
                    <select
                      id={`ep-cf-${def.field}`}
                      value={customFields[def.field] ?? ''}
                      onChange={(e) =>
                        setCustomFields((prev) => ({ ...prev, [def.field]: e.target.value }))
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">{t('— none —')}</option>
                      {Object.entries(def.values).map(([key, opt]) => (
                        <option key={key} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={`ep-cf-${def.field}`}
                      value={customFields[def.field] ?? ''}
                      onChange={(e) =>
                        setCustomFields((prev) => ({ ...prev, [def.field]: e.target.value }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>
          )}
          {/* App settings — same component the NewPatientDialog uses,
              seeded from the patient's current `flags_raw`. Renders
              nothing when the clinic/clinician context isn't passed in
              (so the legacy callers that don't yet provide it stay
              functional) or when no overridable flags are visible to
              this clinic. */}
          {clinic && clinicianFlags && (
            <div className="border-t pt-3">
              <PatientFlagsSection
                clinic={clinic}
                clinicianFlags={clinicianFlags}
                value={flags}
                onChange={setFlags}
              />
            </div>
          )}
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('Saving…') : t('Save changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
